import { FC, useCallback, useEffect } from 'react';
import { Button } from '@dynamika-shared/ui/Button/Button';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';

type UnscheduledVisitOrderPopoverProps = {
  open: boolean;
  onClose: () => void;
  wrapperRef: React.RefObject<HTMLDivElement>;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onConfirmPosition: () => void;
  okButtonProps: { disabled: boolean };
  controlsProps: {
    isLeftDisabled?: boolean;
    isRightDisabled?: boolean;
  };
};

const backdropStyle =
  'absolute -top-[3px] left-0 w-full h-full backdrop-blur-md bg-white/40 rounded-t-md z-50';
const menuContainerStyle =
  'flex gap-4 absolute w-full p-2 bg-white rounded-[16px]';
const buttonStyle =
  'group grow h-5 !text-1424 font-medium bg-redOrange bg-opacity-40 !border-brightGray !border-1';

export const UnscheduledVisitOrderPopover: FC<
  UnscheduledVisitOrderPopoverProps
> = ({
  open,
  onClose,
  wrapperRef,
  onMoveLeft,
  onMoveRight,
  onConfirmPosition,
  okButtonProps: { disabled },
  controlsProps,
}) => {
  const onEscapePress = useCallback(
    (event: KeyboardEvent) => event.key === 'Escape' && onClose?.(),
    [onClose]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef?.current?.contains(event.target as HTMLDivElement)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', onEscapePress);
    }
    return () => {
      if (open) {
        window.removeEventListener('keydown', onEscapePress);
      }
    };
  }, [open, onEscapePress]);

  if (!open) return null;

  return (
    <div className={backdropStyle}>
      <div className={menuContainerStyle}>
        <Button
          className={buttonStyle}
          icon={<ChevronIcon className={'group-hover:text-white'} />}
          disabled={controlsProps.isLeftDisabled}
          onClick={onMoveLeft}
        >
          Move Left
        </Button>
        <Button
          type={'primary'}
          className={'h-5 !text-1424 font-medium !px-3'}
          disabled={disabled}
          onClick={onConfirmPosition}
        >
          Confirm Visit Position
        </Button>
        <Button
          className={buttonStyle}
          icon={<ChevronIcon dir={'right'} className={'group-hover:text-white'} />}
          disabled={controlsProps.isRightDisabled}
          iconPosition={'end'}
          onClick={onMoveRight}
        >
          Move Right
        </Button>
      </div>
      <div className='absolute inset-0 -z-10' onClick={onClose}></div>
    </div>
  );
};
